import React, { useEffect, useState, useRef, useMemo } from "react";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import { debounce } from "@mui/material/utils";
import { SORT_LIST } from "../util/compare_utils";
import { LoansDesktopView } from "./comparison_views/LoansDesktopView";
import { LoansMobileView } from "./comparison_views/LoansMobileView";
import { IrqSettingsModal } from "./modals/IrqSettingsModal";
import { getDateFourteenDaysFromNow } from "../util/getDateFourteenDaysFromNow";
import { useJsApiLoader } from "@react-google-maps/api";
import getCounty from "../util/getCounty";

const autocompleteService = { current: null };

const FastTrack = ({
  data,
  customFormEdit,
  onFormEdit,
  errors,
  content,
  runQuotesQuery,
  isValid,
}) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const defaultAddress = params.get("address") ?? "";

  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState(defaultAddress);
  const [options, setOptions] = useState([]);
  const addressUpdatedFromQueryRef = useRef(false);

  const loanOptions = [
    "10Fixed",
    "15Fixed",
    "20Fixed",
    "30Fixed",
    "ARM56",
    "ARM76",
    "ARM106",
  ];

  const placesServiceRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["maps", "places"],
  });

  // Debounced fetch
  const fetch = useMemo(
    () =>
      debounce((request, callback) => {
        if (autocompleteService.current) {
          autocompleteService.current.getPlacePredictions(request, callback);
        }
      }, 400),
    []
  );

  // Load autocomplete service
  useEffect(() => {
    if (isLoaded && !autocompleteService.current && window.google) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }
  }, [isLoaded]);

  // Handle query param form defaults
  useEffect(() => {
    if (params.toString().length > 0) {
      const rawLookingTo = params.get("looking_to");
      const wordCasedLookingTo =
        rawLookingTo?.charAt(0).toUpperCase() + rawLookingTo?.slice(1);
      const lookingTo = wordCasedLookingTo ?? "Purchase";
      customFormEdit("price", params.get("purchase_price"));
      customFormEdit("creditScore", params.get("credit_score"));
      customFormEdit("lookingTo", lookingTo);
      customFormEdit("propertyType", "Single");
      customFormEdit("occupancyType", "Primary");
      customFormEdit("plannedClosingDate", getDateFourteenDaysFromNow());
      customFormEdit("forSaleByOwner", "No");
      customFormEdit("hasRealEstateAgent", "Yes");
      customFormEdit("hasWaivedEscrow", "No");
      customFormEdit("isBuyingAsIs", "No");
      customFormEdit("isSelfEmployed", false);
      customFormEdit("isUsCitizen", true);
      customFormEdit("isVeteran", false);
      customFormEdit("loanOptions", loanOptions);

      const firstTimeBuyer = localStorage.getItem("firstTimeBuyer");
      if (!firstTimeBuyer) {
        localStorage.setItem("firstTimeBuyer", "false");
      }
      if (lookingTo === "Purchase" || lookingTo === "Build") {
        customFormEdit("downPayment", params.get("down_payment"));
      } else {
        customFormEdit(
          "existingLoanBalance",
          params.get("existing_loan_balance")
        );
      }
    }
  }, []);

  // Fetch predictions when inputValue changes
  useEffect(() => {
    let active = true;

    if (!inputValue || !isLoaded) return;

    fetch({ input: inputValue }, (results) => {
      if (active) {
        let newOptions = value ? [value] : [];
        if (results) newOptions = [...newOptions, ...results];
        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue, fetch, value, isLoaded]);

  // Auto-select first match if address from query string
  useEffect(() => {
    if (
      !defaultAddress ||
      !isLoaded ||
      !autocompleteService.current ||
      !placesServiceRef.current ||
      addressUpdatedFromQueryRef.current
    )
      return;

    autocompleteService.current.getPlacePredictions(
      { input: defaultAddress },
      (predictions, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions?.length > 0
        ) {
          const first = predictions[0];
          const placesService = new window.google.maps.places.PlacesService(
            placesServiceRef.current
          );

          placesService.getDetails(
            { placeId: first.place_id },
            async (place, placeStatus) => {
              if (
                placeStatus === window.google.maps.places.PlacesServiceStatus.OK
              ) {
                // Guard against unnecessary re-renders
                if (first.description !== inputValue) {
                  setValue(first);
                  setInputValue(first.description);

                  const components = place.address_components || [];
                  const getComponent = (type) =>
                    components.find((c) => c.types.includes(type)) || {};

                  const streetNumber =
                    getComponent("street_number").long_name || "";
                  const route = getComponent("route").long_name || "";
                  const localityObj = getComponent("locality");
                  const sublocalityObj = getComponent("sublocality");
                  const neighborhoodObj = getComponent("neighborhood");
                  const city =
                    localityObj.long_name ||
                    sublocalityObj.long_name ||
                    neighborhoodObj.long_name;
                  const neighborhood =
                    localityObj.long_name || sublocalityObj.long_name
                      ? neighborhoodObj.long_name
                      : undefined;

                  // Get latitude and longitude for county lookup
                  const location = place.geometry?.location;
                  const lat = location?.lat();
                  const lng = location?.lng();
                  const { county, countyFips, stateFips } = await getCounty(
                    place.address_components,
                    lat,
                    lng
                  );

                  const state =
                    getComponent("administrative_area_level_1").short_name ||
                    "";
                  const zip = getComponent("postal_code").long_name || "";

                  const street = `${streetNumber} ${route}`.trim();

                  customFormEdit("street", street);
                  customFormEdit("city", city);
                  customFormEdit("county", county);
                  customFormEdit("countyFips", countyFips);
                  customFormEdit("stateFips", stateFips);
                  customFormEdit("state", state);
                  customFormEdit("zip", zip);
                  customFormEdit("neighborhood", neighborhood);

                  const firstTimeBuyer = localStorage.getItem("firstTimeBuyer");
                  if (!firstTimeBuyer) {
                    localStorage.setItem("firstTimeBuyer", "false");
                  }
                  if (
                    typeof runQuotesQuery === "function" &&
                    !addressUpdatedFromQueryRef.current
                  ) {
                    addressUpdatedFromQueryRef.current = true; // Prevent future updates
                    runQuotesQuery("Purchase", {
                      city,
                      creditScore:
                        data.creditScore ?? params.get("credit_score"),
                      downPayment:
                        data.downPayment ?? params.get("down_payment"),
                      hasWaivedEscrow: data.hasWaivedEscrow ?? "No",
                      occupancyType: data.occupancyType ?? "Primary",
                      isSelfEmployed: data.isSelfEmployed ?? false,
                      isUsCitizen: data.isUsCitizen ?? true,
                      lookAtVaLoans: data.lookAtVaLoans ?? false,
                      disabledVeteran: data.disabledVeteran ?? false,
                      vaFirstTimeUse: data.vaFirstTimeUse ?? false,
                      loanOptions,
                      plannedClosingDate:
                        data.plannedClosingDate ?? getDateFourteenDaysFromNow(),
                      price: data.price ?? params.get("purchase_price"),
                      propertyType: data.propertyType ?? "Single",
                      state,
                      street,
                      zip,
                      county,
                      neighborhood,
                      amountOfCashOut: data.amountOfCashOut ?? 0,
                      veteranType: data.veteranType ?? false,
                    });
                  }
                }
              }
            }
          );
        }
      }
    );
  }, [defaultAddress, isLoaded]);

  if (!isLoaded) return null;

  return (
    <>
      <div ref={placesServiceRef} style={{ display: "none" }} />
      <LoansDesktopView
        hideTopBar
        isGrid
        setGrid={() => {}}
        isComparedIds={[]}
        setComparedId={() => {}}
        sorting={SORT_LIST[0]}
        setSorting={() => {}}
        sortedLoans={[]}
        setIsComparisonView={() => {}}
        setDetails={() => {}}
        setShowSettingsModal={() => {}}
        comparedQuotes={[]}
        showShareModal={false}
        setShowShareModal={() => {}}
        onFormEdit={onFormEdit}
        showReviewModal={false}
        setShowReviewModal={() => {}}
        enteredData={data}
        content={content}
      />
      <LoansMobileView
        hideTopBar
        isGrid
        setGrid={() => {}}
        isComparedIds={[]}
        setComparedId={() => {}}
        sorting={SORT_LIST[0]}
        setSorting={() => {}}
        sortedLoans={[]}
        setIsComparisonView={() => {}}
        comparedQuotes={[]}
        setDetails={() => {}}
        setShowSettingsModal={() => {}}
        enteredData={data}
      />
      <IrqSettingsModal
        isFastTrack
        open
        runQuotesQuery={runQuotesQuery}
        onClose={() => {}}
        errors={errors}
        customFormEdit={customFormEdit}
        onFormEdit={onFormEdit}
        isLoading={false}
        enteredData={data}
        isValid={isValid}
      />
    </>
  );
};

export const FastTrackView = connect()(FastTrack);
